import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { INTEGRATIONS_WITH_DYNAMIC_SCHEMA, IntegrationServiceId } from '../../common/integrations'

import {
  DocumentProspectingDetailsFormProps,
  DocumentProspectingPageType,
  ProspectingBaseFormValues,
  ProspectingDetailsFormValues,
} from '../../models/DocumentProspecting'
import { User } from '../../models/User'

import { RootState } from '../store'
import { FileInfo } from '../media/file'
import { type AIAgentAttachment } from '../ai/aiAgent'

interface DocumentProspectingState {
  selectedFileName: string | null
  selectedFileSize: number | null
  fileInfo: FileInfo | null
  attachment: AIAgentAttachment | null
  uploadIsLoading: boolean
  currentUser: User | null
  currentUserIsLoading: boolean
  contact: User | null
  contactIsLoading: boolean
  serviceId: IntegrationServiceId | null
  successIntegrations: IntegrationServiceId[]
  integrationContact: User | null
  integrationContactErrors: Record<string, unknown> | null
  baseFormValues: ProspectingBaseFormValues | null
  baseFormValuesLoading: boolean
  detailsFormValues: ProspectingDetailsFormValues | null
  detailsFormValuesLoading: boolean
  detailsFormProps: DocumentProspectingDetailsFormProps | null
  detailsFormPropsLoading: boolean
  page: DocumentProspectingPageType
}

const initialState: DocumentProspectingState = {
  selectedFileName: null,
  selectedFileSize: null,
  fileInfo: null,
  attachment: null,
  uploadIsLoading: false,
  currentUser: null,
  currentUserIsLoading: false,
  contact: null,
  contactIsLoading: false,
  serviceId: null,
  successIntegrations: [],
  integrationContact: null,
  integrationContactErrors: null,
  baseFormValues: null,
  baseFormValuesLoading: false,
  detailsFormValues: null,
  detailsFormValuesLoading: false,
  detailsFormProps: null,
  detailsFormPropsLoading: false,
  page: DocumentProspectingPageType.PROSPECTING_FILE_UPLOAD,
}

export const documentProspectingSlice = createSlice({
  name: 'documentProspecting',
  initialState,
  reducers: {
    setDocumentProspectingSelectedFileName: (state, action: PayloadAction<string>) => {
      state.selectedFileName = action.payload
    },
    setDocumentProspectingSelectedFileSize: (state, action: PayloadAction<number>) => {
      state.selectedFileSize = action.payload
    },
    setDocumentProspectingFileInfo: (state, action: PayloadAction<FileInfo>) => {
      state.fileInfo = action.payload
    },
    setDocumentProspectingAttachment: (state, action: PayloadAction<AIAgentAttachment>) => {
      state.attachment = action.payload
    },
    setDocumentProspectingUploadIsLoading: (state, action: PayloadAction<boolean>) => {
      state.uploadIsLoading = action.payload
    },
    setDocumentProspectingCurrentUser: (state, action: PayloadAction<User>) => {
      state.currentUser = action.payload
    },
    setDocumentProspectingCurrentUserIsLoading: (state, action: PayloadAction<boolean>) => {
      state.currentUserIsLoading = action.payload
    },
    setDocumentProspectingContact: (state, action: PayloadAction<User>) => {
      state.contact = action.payload
    },
    setDocumentProspectingContactLoading: (state, action: PayloadAction<boolean>) => {
      state.contactIsLoading = action.payload
    },
    setDocumentProspectingServiceId: (state, action: PayloadAction<IntegrationServiceId>) => {
      state.serviceId = action.payload
    },
    setDocumentProspectingIntegrationAsSuccess: (state, action: PayloadAction<IntegrationServiceId>) => {
      state.successIntegrations.push(action.payload)
    },
    setDocumentProspectingIntegrationContact: (state, action: PayloadAction<User>) => {
      state.integrationContact = action.payload
    },
    setDocumentProspectingIntegrationContactErrors: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.integrationContactErrors = action.payload
    },
    setDocumentProspectingBaseFormValues: (state, action: PayloadAction<ProspectingBaseFormValues>) => {
      state.baseFormValues = action.payload
    },
    setDocumentProspectingBaseFormValuesLoading: (state, action: PayloadAction<boolean>) => {
      state.baseFormValuesLoading = action.payload
    },
    setDocumentProspectingDetailsFormValues: (state, action: PayloadAction<ProspectingDetailsFormValues>) => {
      state.detailsFormValues = action.payload
    },
    setDocumentProspectingDetailsFormValuesLoading: (state, action: PayloadAction<boolean>) => {
      state.detailsFormValuesLoading = action.payload
    },
    setDocumentProspectingDetailsFormProps: (state, action: PayloadAction<DocumentProspectingDetailsFormProps>) => {
      state.detailsFormProps = action.payload
    },
    setDocumentProspectingDetailsFormPropsLoading: (state, action: PayloadAction<boolean>) => {
      state.detailsFormPropsLoading = action.payload
    },
    clearDocumentProspectingDetailsForm: (state) => {
      state.detailsFormValues = null
      state.detailsFormProps = null
      state.integrationContact = null
      state.integrationContactErrors = null
    },
    setDocumentProspectingPage: (state, action: PayloadAction<DocumentProspectingPageType>) => {
      state.page = action.payload
    },
    resetDocumentProspecting: () => initialState,
  },
})

export const {
  setDocumentProspectingSelectedFileName,
  setDocumentProspectingSelectedFileSize,
  setDocumentProspectingFileInfo,
  setDocumentProspectingAttachment,
  setDocumentProspectingUploadIsLoading,
  setDocumentProspectingCurrentUser,
  setDocumentProspectingCurrentUserIsLoading,
  setDocumentProspectingContact,
  setDocumentProspectingContactLoading,
  setDocumentProspectingServiceId,
  setDocumentProspectingIntegrationAsSuccess,
  setDocumentProspectingIntegrationContact,
  setDocumentProspectingIntegrationContactErrors,
  setDocumentProspectingBaseFormValues,
  setDocumentProspectingBaseFormValuesLoading,
  setDocumentProspectingDetailsFormValues,
  setDocumentProspectingDetailsFormValuesLoading,
  setDocumentProspectingDetailsFormProps,
  setDocumentProspectingDetailsFormPropsLoading,
  clearDocumentProspectingDetailsForm,
  setDocumentProspectingPage,
  resetDocumentProspecting,
} = documentProspectingSlice.actions

export const selectDocumentProspectingSelectedFileName = (state: RootState) => state.processDocument.documentProspecting.selectedFileName

export const selectDocumentProspectingSelectedFileSize = (state: RootState) => state.processDocument.documentProspecting.selectedFileSize

export const selectDocumentProspectingFileInfo = (state: RootState) => state.processDocument.documentProspecting.fileInfo

export const selectDocumentProspectingAttachment = (state: RootState) => state.processDocument.documentProspecting.attachment

export const selectDocumentProspectingUploadIsLoading = (state: RootState) => state.processDocument.documentProspecting.uploadIsLoading

export const selectDocumentProspectingContact = (state: RootState) => state.processDocument.documentProspecting.contact

export const selectDocumentProspectingContactLoading = (state: RootState) => state.processDocument.documentProspecting.contactIsLoading

export const selectDocumentProspectingCurrentUser = (state: RootState) => state.processDocument.documentProspecting.currentUser

export const selectDocumentProspectingCurrentUserLoading = (state: RootState) => state.processDocument.documentProspecting.currentUserIsLoading

export const selectDocumentProspectingServiceId = (state: RootState) => state.processDocument.documentProspecting.serviceId

export const selectDocumentProspectingSuccessIntegrations = (state: RootState) => state.processDocument.documentProspecting.successIntegrations

export const selectDocumentProspectingIntegrationContact = (state: RootState) => state.processDocument.documentProspecting.integrationContact

export const selectDocumentProspectingIntegrationContactErrors = (state: RootState) => state.processDocument.documentProspecting.integrationContactErrors

export const selectDocumentProspectingBaseFormValues = (state: RootState) => state.processDocument.documentProspecting.baseFormValues

export const selectDocumentProspectingBaseFormValuesLoading = (state: RootState) => state.processDocument.documentProspecting.baseFormValuesLoading

export const selectDocumentProspectingDetailsFormValues = (state: RootState) => state.processDocument.documentProspecting.detailsFormValues

export const selectDocumentProspectingDetailsFormProps = (state: RootState) => state.processDocument.documentProspecting.detailsFormProps

export const selectDocumentProspectingDetailsFormPropsLoading = (state: RootState) => state.processDocument.documentProspecting.detailsFormPropsLoading

export const selectDocumentProspectingPage = (state: RootState) => state.processDocument.documentProspecting.page

export const selectDocumentProspectingDetailsFormLoading = (state: RootState) =>
  state.processDocument.documentProspecting.detailsFormValuesLoading || state.processDocument.documentProspecting.detailsFormPropsLoading

export const selectDocumentProspectingIntegrationHasDynamicSchema = (state: RootState) => {
  const { serviceId } = state.processDocument.documentProspecting
  if (!serviceId) {
    return false
  }

  return INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId)
}

export const selectDocumentProspectingIntegrationContactExternalId = (state: RootState) => {
  const { integrationContact, serviceId } = state.processDocument.documentProspecting
  if (!integrationContact || !serviceId) {
    return null
  }

  const integration = integrationContact.integrations?.find((integration) => integration.serviceId === serviceId)
  if (!integration) {
    return null
  }

  const { externalId } = integration
  return externalId
}
